// AuthPage.js - iOS WebKit compatible version (no Framer Motion, no Shadcn UI on form)
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Mail, 
  Lock, 
  User, 
  Eye, 
  EyeOff, 
  CheckCircle, 
  Loader2,
  Shield,
  ArrowRight
} from 'lucide-react';
import LeonAvatar from '../components/LeonAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const benefits = [
  { icon: CheckCircle, text: 'Audit intelligent de devis' },
  { icon: CheckCircle, text: 'Estimation réaliste des budgets travaux' },
  { icon: CheckCircle, text: 'Recettes travaux détaillées étape par étape' },
];

export default function AuthPage() {
  const { login, isAuthenticated, setAuthenticatedUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Form fields - primitive strings only
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      console.log('[AuthPage] Already authenticated, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // iOS WebKit compatible handler - no event object
  const handleEmailAuth = async () => {
    // Extract primitives immediately to avoid any cloning issues
    const formMode = String(mode);
    const formEmail = String(email || '').trim();
    const formPassword = String(password || '');
    const formName = String(name || '').trim();
    const formConfirmPassword = String(confirmPassword || '');
    const formTermsAccepted = Boolean(termsAccepted);
    
    setError(null);
    
    // Validation
    if (formMode === 'register') {
      if (!formName) {
        setError('Veuillez entrer votre nom');
        return;
      }
      if (formPassword !== formConfirmPassword) {
        setError('Les mots de passe ne correspondent pas');
        return;
      }
      if (formPassword.length < 6) {
        setError('Le mot de passe doit contenir au moins 6 caractères');
        return;
      }
      if (!formTermsAccepted) {
        setError('Veuillez accepter les conditions générales d\'utilisation');
        return;
      }
    }
    
    if (!formEmail || !formPassword) {
      setError('Veuillez remplir tous les champs');
      return;
    }
    
    setLoading(true);
    // IMPORTANT: Concaténation de strings uniquement (pas de virgules avec console.log)
    console.log('[AuthPage] Starting ' + formMode + ' for: ' + formEmail);
    
    try {
      const endpoint = formMode === 'register' ? '/api/auth/register' : '/api/auth/login';
      const body = formMode === 'register'
        ? { name: formName, email: formEmail, password: formPassword }
        : { email: formEmail, password: formPassword };
      
      console.log('[AuthPage] Calling ' + API_URL + endpoint);
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      
      console.log('[AuthPage] Response status: ' + response.status);
      
      // Parse response body
      let data = null;
      let parseError = null;
      try {
        const text = await response.text();
        console.log('[AuthPage] Response text length: ' + (text?.length || 0));
        if (text) {
          data = JSON.parse(text);
        }
      } catch (parseErr) {
        parseError = parseErr?.message || String(parseErr);
        console.log('[AuthPage] Parse error: ' + parseError);
      }
      
      // Handle HTTP errors with precise messages
      if (!response.ok) {
        let errorMsg;
        
        // Use backend error message if available
        if (data?.detail) {
          errorMsg = data.detail;
        } else if (response.status === 401) {
          errorMsg = 'Email ou mot de passe incorrect.';
        } else if (response.status === 400) {
          errorMsg = 'Données invalides. Vérifiez vos informations.';
        } else if (response.status === 404) {
          errorMsg = 'Service temporairement indisponible.';
        } else if (response.status === 500) {
          errorMsg = 'Erreur serveur. Veuillez réessayer dans quelques instants.';
        } else if (response.status === 503) {
          errorMsg = 'Service en maintenance. Veuillez réessayer plus tard.';
        } else if (parseError) {
          errorMsg = 'Réponse serveur invalide. Veuillez réessayer.';
        } else {
          errorMsg = `Erreur de connexion (${response.status}). Veuillez réessayer.`;
        }
        
        console.log('[AuthPage] Error: ' + errorMsg + ' | Status: ' + response.status + ' | Detail: ' + String(data?.detail || 'none'));
        setError(errorMsg);
        setLoading(false);
        return;
      }
      
      // Validate response data
      if (!data) {
        console.log('[AuthPage] No data received from server');
        setError('Réponse vide du serveur. Veuillez réessayer.');
        setLoading(false);
        return;
      }
      
      if (!data.session_token) {
        console.log('[AuthPage] No session_token in response');
        setError('Erreur d\'authentification : token manquant. Veuillez réessayer.');
        setLoading(false);
        return;
      }
      
      if (!data.email) {
        console.log('[AuthPage] No email in response');
        setError('Erreur de récupération du profil. Veuillez réessayer.');
        setLoading(false);
        return;
      }
      
      // ============================================
      // SUCCESS - CRITICAL FLOW
      // ============================================
      // IMPORTANT: Créer un objet propre avec uniquement des primitives
      // pour éviter DataCloneError lors de la sérialisation React
      const cleanUserData = {
        user_id: String(data.user_id || ''),
        email: String(data.email || ''),
        name: String(data.name || ''),
        picture: data.picture ? String(data.picture) : null,
        subscription_plan: String(data.subscription_plan || 'free'),
        session_token: String(data.session_token || '')
      };
      
      console.log('[AuthPage] ✓ Login successful for: ' + cleanUserData.email);
      console.log('[AuthPage] Token received: ' + cleanUserData.session_token.substring(0, 25) + '...');
      console.log('[AuthPage] Subscription plan: ' + cleanUserData.subscription_plan);
      
      // 1. Set authenticated state directly (including token storage)
      setAuthenticatedUser(cleanUserData, cleanUserData.session_token);
      
      // 2. Verify token was stored
      const storedToken = localStorage.getItem('abl_session_token');
      console.log('[AuthPage] Token verified in localStorage: ' + (storedToken ? 'YES' : 'NO'));
      
      if (!storedToken) {
        console.log('[AuthPage] Failed to store token in localStorage');
        setError('Erreur de stockage de session. Vérifiez que les cookies/localStorage sont activés.');
        setLoading(false);
        return;
      }
      
      // 3. Use React Router navigate instead of full page reload
      console.log('[AuthPage] Navigating to /dashboard via React Router...');
      setLoading(false);
      navigate('/dashboard', { replace: true });
      
    } catch (err) {
      // IMPORTANT: Ne jamais passer d'objets à console.log/error
      // car emergent-main.js intercepte et tente de cloner via postMessage
      const errMsg = String(err?.message || err || 'Unknown error');
      const errName = String(err?.name || 'UnknownError');
      const errStack = String(err?.stack || 'No stack trace').substring(0, 200);
      // Utiliser concaténation, pas virgules ni template literals avec objets
      console.log('[AuthPage] Exception: ' + errName + ' - ' + errMsg);
      console.log('[AuthPage] Stack: ' + errStack);
      
      let errorMsg;
      
      // Precise error messages based on error type
      if (errName === 'TypeError' && errMsg.includes('fetch')) {
        errorMsg = 'Impossible de contacter le serveur. Vérifiez votre connexion internet.';
      } else if (errName === 'AbortError') {
        errorMsg = 'La requête a été annulée. Veuillez réessayer.';
      } else if (errMsg.includes('NetworkError') || errMsg.includes('network')) {
        errorMsg = 'Erreur réseau. Vérifiez votre connexion internet.';
      } else if (errMsg.includes('timeout') || errMsg.includes('Timeout')) {
        errorMsg = 'Le serveur met trop de temps à répondre. Veuillez réessayer.';
      } else if (errMsg.includes('JSON')) {
        errorMsg = 'Réponse serveur invalide. Veuillez réessayer.';
      } else if (errMsg.includes('localStorage') || errMsg.includes('storage')) {
        errorMsg = 'Erreur de stockage. Vérifiez que les cookies sont activés.';
      } else if (errMsg.includes('clone') || errName.includes('Clone')) {
        // DataCloneError spécifique
        errorMsg = 'Erreur de synchronisation interne. Veuillez réessayer ou vider le cache du navigateur.';
      } else if (errMsg.includes('CORS') || errMsg.includes('cross-origin')) {
        errorMsg = 'Erreur de communication avec le serveur. Veuillez rafraîchir la page.';
      } else {
        // Fallback avec plus de contexte - montrer l'erreur exacte pour debug
        errorMsg = 'Erreur technique: ' + errMsg.substring(0, 100) + '. Veuillez réessayer.';
      }
      
      setError(errorMsg);
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    login();
  };

  // Input style classes
  const inputClasses = "w-full pl-10 pr-10 py-3 bg-zinc-800/50 border border-zinc-700 text-white placeholder:text-zinc-500 rounded-xl focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-500/20 transition-colors";

  return (
    <div className="min-h-screen bg-black-rich flex">
      {/* ==================== LEFT COLUMN - BRANDING (Desktop only) ==================== */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-gold-500/10 via-black-rich to-black-rich" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-gold-500/20 via-transparent to-transparent" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-16 py-12">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 mb-12">
            <img 
              src="/assets/phenix-logo.png" 
              alt="Assistant by Léon" 
              className="h-12 w-auto"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span className="text-2xl font-heading font-bold">
              <span className="text-gold-500">Assistant by Léon</span>
            </span>
          </Link>
          
          {/* Main headline */}
          <div>
            <h1 className="text-4xl lg:text-5xl font-heading font-bold text-white leading-tight mb-6">
              Auditez vos devis et{' '}
              <span className="text-gold-500">sécurisez</span>{' '}
              vos travaux.
            </h1>
            
            <p className="text-lg text-zinc-400 mb-10 leading-relaxed max-w-lg">
              Assistant by Léon vous aide à analyser vos devis, estimer vos budgets travaux 
              et obtenir des méthodes claires pour réussir vos projets de rénovation.
            </p>
          </div>
          
          {/* Benefits */}
          <div className="space-y-4 mb-12">
            {benefits.map((benefit) => (
              <div key={benefit.text} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-gold-500/20 flex items-center justify-center">
                  <benefit.icon className="w-4 h-4 text-gold-500" />
                </div>
                <span className="text-white font-medium">{benefit.text}</span>
              </div>
            ))}
          </div>
          
          {/* Léon mascot */}
          <div className="flex items-center gap-4 p-5 bg-zinc-900/50 border border-zinc-800 rounded-2xl max-w-md">
            <LeonAvatar className="w-14 h-14 flex-shrink-0" />
            <div>
              <p className="text-gold-500 font-bold text-sm mb-1">Léon, votre assistant</p>
              <p className="text-zinc-400 text-sm">
                Je suis là pour vous aider à comprendre vos devis et éviter les pièges.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ==================== RIGHT COLUMN - FORM ==================== */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <img 
                src="/assets/phenix-logo.png" 
                alt="Assistant by Léon" 
                className="h-10 w-auto"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <span className="text-xl font-heading font-bold">
                <span className="text-gold-500">Assistant by Léon</span>
              </span>
            </Link>
          </div>

          {/* Form card */}
          <div className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-3xl p-8 shadow-2xl">
            {/* Tabs */}
            <div className="flex gap-2 mb-8 p-1 bg-zinc-800/50 rounded-xl">
              <button
                type="button"
                onClick={() => { setMode('login'); setError(null); }}
                className={`flex-1 py-3 px-4 rounded-lg text-sm font-bold transition-all ${
                  mode === 'login'
                    ? 'bg-gold-500 text-black'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                Connexion
              </button>
              <button
                type="button"
                onClick={() => { setMode('register'); setError(null); }}
                className={`flex-1 py-3 px-4 rounded-lg text-sm font-bold transition-all ${
                  mode === 'register'
                    ? 'bg-gold-500 text-black'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                Créer un compte
              </button>
            </div>

            <div key={mode}>
              {/* Form title */}
              <h2 className="text-2xl font-heading font-bold text-white mb-2">
                {mode === 'login' ? 'Connexion' : 'Créer un compte'}
              </h2>
              <p className="text-zinc-400 text-sm mb-6">
                {mode === 'login' 
                  ? 'Accédez à votre espace personnel'
                  : 'Rejoignez Assistant by Léon gratuitement'
                }
              </p>

              {/* Error message */}
              {error && (
                <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              {/* Form - no onSubmit, pure button click */}
              <div className="space-y-4">
                {/* Name field (register only) */}
                {mode === 'register' && (
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      Prénom ou pseudo
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Votre prénom ou pseudo"
                        className={inputClasses}
                        data-testid="auth-name-input"
                      />
                    </div>
                  </div>
                )}

                {/* Email field */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    Adresse email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="vous@exemple.com"
                      className={inputClasses}
                      data-testid="auth-email-input"
                    />
                  </div>
                </div>

                {/* Password field */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    Mot de passe
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className={inputClasses}
                      data-testid="auth-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Confirm password (register only) */}
                {mode === 'register' && (
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      Confirmer le mot de passe
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="••••••••"
                        className={inputClasses}
                        data-testid="auth-confirm-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                )}

                {/* Terms checkbox (register only) */}
                {mode === 'register' && (
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="mt-1 w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-gold-500 focus:ring-gold-500/20"
                      data-testid="auth-terms-checkbox"
                    />
                    <label htmlFor="terms" className="text-sm text-zinc-400">
                      J'accepte les{' '}
                      <Link to="/conditions-generales" className="text-gold-500 hover:underline">
                        conditions générales d'utilisation
                      </Link>
                    </label>
                  </div>
                )}

                {/* Forgot password (login only) */}
                {mode === 'login' && (
                  <div className="text-right">
                    <button
                      type="button"
                      className="text-sm text-gold-500 hover:underline"
                    >
                      Mot de passe oublié ?
                    </button>
                  </div>
                )}

                {/* Submit button - native HTML button, no Shadcn */}
                <button
                  type="button"
                  onClick={handleEmailAuth}
                  disabled={loading}
                  className="w-full h-12 bg-gold-500 hover:bg-gold-400 text-black font-bold rounded-xl text-base transition-all hover:shadow-lg hover:shadow-gold-500/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="auth-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Chargement...
                    </>
                  ) : (
                    <>
                      {mode === 'login' ? 'Se connecter' : 'Créer mon compte'}
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-zinc-700" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-zinc-900 px-3 text-zinc-500">ou</span>
                </div>
              </div>

              {/* Google login - native HTML button */}
              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={loading}
                className="w-full h-12 bg-white hover:bg-gray-100 text-black font-medium rounded-xl border-0 transition-all flex items-center justify-center gap-2"
                data-testid="auth-google-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continuer avec Google
              </button>
            </div>

            {/* Security message */}
            <div className="mt-6 flex items-center justify-center gap-2 text-zinc-500 text-xs">
              <Shield className="w-4 h-4" />
              <span>Vos données sont sécurisées et ne sont jamais partagées.</span>
            </div>
          </div>

          {/* Back to home link */}
          <div className="mt-6 text-center">
            <Link to="/" className="text-zinc-500 hover:text-gold-500 text-sm transition-colors">
              ← Retour à l'accueil
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
